import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, sendToast } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { buildMyAccountUrl } from '../utils/my-account-url';

import '../components/access-setup/access-granted-toast.scss';

const ACCESS_GRANTED_TOAST_ID = 'toast-access-granted';

/**
 * PSTUDIO-4098. Shows the persistent "Access granted" toast in place, after the
 * access screen has assigned the role AND the project has then opened. Returns
 * `arm`, which the access screen calls on a grant; the toast is shown on the
 * next render in which `projectOpened` is true, and then disarmed.
 *
 * Mounted in `Root`, not in the access screen: the screen unmounts the moment the
 * machine leaves `accessDenied`, which is before the project has loaded.
 *
 * One toast per `arm`: the pending flag is a ref cleared in the same effect run
 * that sends the toast, so a re-render, a StrictMode replay or a later reload of
 * the project finds it cleared. LIMIT: the flag lives in this component's memory —
 * a reload of the page after the grant and before the project opened shows no
 * toast (the project itself still opens, since the role is already assigned).
 */
export const useAccessGrantedToastOnOpen = (
  projectOpened: boolean
): (() => void) => {
  const { t } = useTranslation();
  const pendingRef = useRef(false);
  // Bumped by `arm` so the effect re-runs even when `projectOpened` did not change.
  const [armCount, setArmCount] = useState(0);

  const defaultOrgId = useProStudioCtxSelector(
    (ctx) => ctx.userInfo?.defaultOrgId
  );
  // The toast outlives this render; the click reads the latest value.
  const defaultOrgIdRef = useRef(defaultOrgId);
  defaultOrgIdRef.current = defaultOrgId;

  const arm = useCallback(() => {
    pendingRef.current = true;
    setArmCount((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!pendingRef.current || !projectOpened) return;
    pendingRef.current = false;

    sendToast({
      toastId: ACCESS_GRANTED_TOAST_ID,
      // Neutral card: no success tint, no type icon (design).
      type: 'info',
      icon: false,
      // `c-toast--wide` sizes the card to its content so the sentence reads on one
      // line; `c-access-granted-toast` carries the Figma offsets and line height.
      className: 'c-toast--wide c-access-granted-toast',
      header: t('pro-studio/common:accessGranted.header') ?? '',
      text: t('pro-studio/common:accessGranted.body') ?? '',
      // `false` is what makes it persistent: no auto-dismiss.
      autoClose: false,
      // `footer` renders the action UNDER the text, aligned to the end.
      footer: (
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            window.location.href = buildMyAccountUrl(
              import.meta.env.FE_MANAGER_URL,
              defaultOrgIdRef.current
            );
          }}
        >
          {t('pro-studio/common:accessGranted.myAccount')}
        </Button>
      ),
    });
  }, [projectOpened, armCount, t]);

  return arm;
};
